// Payments for platform administrators (BL-30, BL-31): M-Pesa payments whose
// account reference matched nobody are listed and matched by hand to a
// company or an individual, and bank transfers are recorded against a company.
import {
  Button,
  Code,
  Group,
  Loader,
  Modal,
  Paper,
  Radio,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { useBrand } from "../../shared/brand/brand";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { parseMoney, useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { AdminLayout } from "./AdminLayout";
import { CompanyPicker } from "./CompanyPicker";

type Payment = Schemas["Payment"];
type Company = Schemas["Company"];
type PayerType = NonNullable<Payment["payer_type"]>;

const UNMATCHED_KEY = ["admin", "payments", "unmatched"] as const;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function AdminPaymentsPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")} intro={t("admin_payments.intro")}>
      <Payments />
    </AdminLayout>
  );
}

function Payments() {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const unmatched = useQuery({ queryKey: UNMATCHED_KEY, queryFn: () => unwrap(api.GET("/admin/payments/unmatched")) });
  const [matching, setMatching] = useState<Payment | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const items = unmatched.data?.items ?? [];

  return (
    <Stack gap="md">
      {notice && <Notice>{notice}</Notice>}
      <Title order={2} size="h4">
        {t("admin_payments.unmatched_title")}
      </Title>
      {unmatched.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : unmatched.isError ? (
        <FormError message={errorMessage(unmatched.error, t("error.generic"))} />
      ) : items.length === 0 ? (
        <Text c="dimmed" data-testid="unmatched-empty">
          {t("admin_payments.unmatched_empty")}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table striped verticalSpacing="sm" aria-label={t("admin_payments.unmatched_title")}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th ta="right">{t("billing.amount")}</Table.Th>
                <Table.Th>{t("admin_payments.provider")}</Table.Th>
                <Table.Th>{t("admin_payments.reference")}</Table.Th>
                <Table.Th>{t("admin_payments.account_reference")}</Table.Th>
                <Table.Th>{t("admin_payments.received")}</Table.Th>
                <Table.Th>{t("admin_companies.actions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((p) => (
                <Table.Tr key={p.id} data-testid="unmatched-row">
                  <Table.Td ta="right">{f.money(p.amount_minor, p.currency)}</Table.Td>
                  <Table.Td>{p.provider}</Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {p.provider_ref}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Code>{p.account_reference || "—"}</Code>
                  </Table.Td>
                  <Table.Td>{f.dateTime(p.paid_at)}</Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => setMatching(p)}
                      aria-label={t("admin_payments.match_for", { reference: p.provider_ref })}
                    >
                      {t("admin_payments.match")}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <MatchDialog
        payment={matching}
        onClose={() => setMatching(null)}
        onMatched={(p) => {
          setMatching(null);
          setNotice(
            t("admin_payments.matched", { reference: p.provider_ref, amount: f.money(p.amount_minor, p.currency) }),
          );
          void queryClient.invalidateQueries({ queryKey: UNMATCHED_KEY });
        }}
      />
      <RecordTransfer
        onRecorded={(p, company) => {
          setNotice(
            t("admin_payments.recorded", {
              amount: f.money(p.amount_minor, p.currency),
              name: company.name,
              reference: p.provider_ref,
            }),
          );
        }}
      />
    </Stack>
  );
}

function MatchDialog({
  payment,
  onClose,
  onMatched,
}: {
  payment: Payment | null;
  onClose: () => void;
  onMatched: (p: Payment) => void;
}) {
  const { t } = useTranslation();
  const f = useFormat();
  const [payerType, setPayerType] = useState<PayerType>("company");
  const [company, setCompany] = useState<Company | null>(null);
  const [accountId, setAccountId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setPayerType("company");
    setCompany(null);
    setAccountId("");
    setError(null);
  };
  const close = () => {
    reset();
    onClose();
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!payment) return;
    const payerId = payerType === "company" ? (company?.id ?? "") : accountId.trim();
    if (payerType === "company" && !payerId) return setError(t("admin_payments.pick_company"));
    if (payerType === "individual" && !UUID.test(payerId)) return setError(t("admin_payments.bad_account_id"));
    setError(null);
    setBusy(true);
    const { data, error } = await api.POST("/admin/payments/{payment_id}/match", {
      params: { path: { payment_id: payment.id } },
      body: { payer_type: payerType, payer_id: payerId },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    reset();
    onMatched(data);
  };

  return (
    <Modal
      opened={payment !== null}
      onClose={close}
      title={t("admin_payments.match_title")}
      centered
      closeButtonProps={{ "aria-label": t("common.close") }}
    >
      <form onSubmit={submit} data-testid="match-form">
        <Stack gap="sm">
          {payment && (
            <Text size="sm">
              {t("admin_payments.match_intro", {
                amount: f.money(payment.amount_minor, payment.currency),
                reference: payment.provider_ref,
              })}
            </Text>
          )}
          <Radio.Group
            label={t("admin_payments.payer_type")}
            value={payerType}
            onChange={(v) => {
              setError(null);
              setPayerType(v as PayerType);
            }}
          >
            <Group gap="md" mt={6}>
              <Radio value="company" label={t("admin_payments.payer_company")} />
              <Radio value="individual" label={t("admin_payments.payer_individual")} />
            </Group>
          </Radio.Group>
          {payerType === "company" ? (
            <CompanyPicker value={company?.id ?? ""} onChange={setCompany} enabled={payment !== null} />
          ) : (
            <TextInput
              label={t("admin_payments.account_id")}
              description={t("admin_payments.account_id_hint")}
              value={accountId}
              onChange={(e) => setAccountId(e.currentTarget.value)}
              required
              spellCheck={false}
              autoComplete="off"
            />
          )}
          <FormError message={error} />
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={busy}>
              {t("admin_payments.match_submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function RecordTransfer({ onRecorded }: { onRecorded: (p: Payment, c: Company) => void }) {
  const { t } = useTranslation();
  const { currency } = useBrand();
  const [company, setCompany] = useState<Company | null>(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!company) return setError(t("admin_payments.pick_company"));
    const minor = parseMoney(amount, currency);
    if (minor === null) return setError(t("admin_payments.bad_amount"));
    setError(null);
    setBusy(true);
    const { data, error } = await api.POST("/admin/payments/manual", {
      body: { company_id: company.id, amount_minor: minor, reference: reference.trim() },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setAmount("");
    setReference("");
    onRecorded(data, company);
    setCompany(null);
  };

  return (
    <Paper withBorder p="md" radius="md">
      <form onSubmit={submit} data-testid="manual-payment-form">
        <Stack gap="sm">
          <Title order={2} size="h4">
            {t("admin_payments.manual_title")}
          </Title>
          <Text size="sm">{t("admin_payments.manual_intro")}</Text>
          <CompanyPicker value={company?.id ?? ""} onChange={setCompany} />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              label={t("admin_payments.amount", { currency })}
              description={t("admin_payments.amount_hint")}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.currentTarget.value)}
              required
            />
            <TextInput
              label={t("admin_payments.bank_reference")}
              value={reference}
              onChange={(e) => setReference(e.currentTarget.value)}
              required
              maxLength={100}
            />
          </SimpleGrid>
          <FormError message={error} />
          <Button type="submit" loading={busy} w="fit-content">
            {t("admin_payments.manual_submit")}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}
